import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import type { ApiResponse } from '@/types';
import type {
	CreateRuleRequest,
	RuleRecord,
	RuleStatus,
	RulesCatalog,
	RunRecord,
	RunsFilter,
	RunsPage,
	TestRequest,
	UpdateRuleRequest,
	MigrationEntry,
	MigrationStatus
} from '@/types/rules.types';

type BuScope = { bu_id?: string };

/** How many runs one Activity page asks for. */
export const RUNS_PAGE_SIZE = 50;

/**
 * Rules (beta), `/ticket-rules`. Every call is brand-scoped: reads pass `bu_id` as a param, writes put
 * it on the URL, as everywhere else that works with a brand's own rows.
 */
export const ticketRulesService = {
	async catalog(scope: BuScope = {}): Promise<RulesCatalog> {
		const response = await api.get<ApiResponse<RulesCatalog>>(API_ENDPOINTS.TICKET_RULES.CATALOG, { params: scope });
		return response.data.data;
	},

	/** The brand's rules in the order they run, each with how often it matched in the last 7 days. */
	async list(scope: BuScope = {}): Promise<RuleRecord[]> {
		const response = await api.get<ApiResponse<RuleRecord[]>>(API_ENDPOINTS.TICKET_RULES.BASE, { params: scope });
		return response.data.data;
	},

	async get(id: string, scope: BuScope = {}): Promise<RuleRecord> {
		const response = await api.get<ApiResponse<RuleRecord>>(API_ENDPOINTS.TICKET_RULES.BY_ID(id), { params: scope });
		return response.data.data;
	},

	async create(body: CreateRuleRequest, scope: BuScope = {}): Promise<RuleRecord> {
		const response = await api.post<ApiResponse<RuleRecord>>(withBuScope(API_ENDPOINTS.TICKET_RULES.BASE, scope), body);
		return response.data.data;
	},

	async update(id: string, body: UpdateRuleRequest, scope: BuScope = {}): Promise<RuleRecord> {
		const response = await api.put<ApiResponse<RuleRecord>>(withBuScope(API_ENDPOINTS.TICKET_RULES.BY_ID(id), scope), body);
		return response.data.data;
	},

	async setStatus(id: string, status: RuleStatus, scope: BuScope = {}): Promise<RuleRecord> {
		const response = await api.patch<ApiResponse<RuleRecord>>(withBuScope(API_ENDPOINTS.TICKET_RULES.STATUS(id), scope), { status });
		return response.data.data;
	},

	async reorder(ids: string[], scope: BuScope = {}): Promise<void> {
		await api.post(withBuScope(API_ENDPOINTS.TICKET_RULES.REORDER, scope), { ids });
	},

	async duplicate(id: string, scope: BuScope = {}): Promise<RuleRecord> {
		const response = await api.post<ApiResponse<RuleRecord>>(withBuScope(API_ENDPOINTS.TICKET_RULES.DUPLICATE(id), scope), {});
		return response.data.data;
	},

	async remove(id: string, scope: BuScope = {}): Promise<void> {
		await api.delete(withBuScope(API_ENDPOINTS.TICKET_RULES.BY_ID(id), scope));
	},

	/** A dry run: nothing is sent or stored. */
	async migrationStatus(scope: BuScope = {}): Promise<MigrationStatus> {
		const response = await api.get<ApiResponse<MigrationStatus>>(API_ENDPOINTS.TICKET_RULES.MIGRATION, { params: scope });
		return response.data.data;
	},

	/** Turns the brand's settings into playbooks, then switches playbooks on. */
	async migrate(scope: BuScope = {}): Promise<MigrationEntry> {
		const response = await api.post<ApiResponse<MigrationEntry>>(withBuScope(API_ENDPOINTS.TICKET_RULES.MIGRATION, scope));
		return response.data.data;
	},

	/** Switches playbooks off; the brand's own settings, never changed, are in charge again. */
	async rollBack(scope: BuScope = {}): Promise<MigrationEntry> {
		const response = await api.post<ApiResponse<MigrationEntry>>(withBuScope(API_ENDPOINTS.TICKET_RULES.ROLLBACK, scope));
		return response.data.data;
	},

	async test(request: TestRequest, scope: BuScope = {}): Promise<RunRecord> {
		const response = await api.post<ApiResponse<RunRecord>>(withBuScope(API_ENDPOINTS.TICKET_RULES.TEST, scope), request);
		return response.data.data;
	},

	async runs(filter: RunsFilter, cursor: string | undefined, scope: BuScope = {}): Promise<RunsPage> {
		const response = await api.get<ApiResponse<RunsPage>>(API_ENDPOINTS.TICKET_RULES.RUNS, {
			params: { ...filter, cursor, limit: RUNS_PAGE_SIZE, ...scope }
		});
		return response.data.data;
	},

	async run(id: string, scope: BuScope = {}): Promise<RunRecord> {
		const response = await api.get<ApiResponse<RunRecord>>(API_ENDPOINTS.TICKET_RULES.RUN_BY_ID(id), { params: scope });
		return response.data.data;
	}
};
